import React, { useEffect, useState } from 'react';
import {
  UsersIcon,
  UserGroupIcon,
  StarIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { DashboardStats } from '../types';
import dashboardService from '../services/dashboardService';
import { AdminLog } from '../types';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  change?: string;
  changeType?: 'positive' | 'negative';
  color: string;
}> = ({ title, value, icon: Icon, change, changeType, color }) => (
  <div className="card">
    <div className="flex items-center">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="ml-4 flex-1">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <p className={`text-sm ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
            {change}
          </p>
        )}
      </div>
    </div>
  </div>
);

const SystemHealthCard: React.FC<{ health: DashboardStats['system_health'] }> = ({ health }) => {
  const getHealthConfig = (health: DashboardStats['system_health']) => {
    switch (health) {
      case 'excellent':
        return { color: 'bg-green-500', text: 'Excelente', icon: ShieldCheckIcon };
      case 'good':
        return { color: 'bg-blue-500', text: 'Bom', icon: ShieldCheckIcon };
      case 'warning':
        return { color: 'bg-yellow-500', text: 'Atenção', icon: ExclamationTriangleIcon };
      case 'critical':
        return { color: 'bg-red-500', text: 'Crítico', icon: ExclamationTriangleIcon };
      default:
        return { color: 'bg-gray-500', text: 'Desconhecido', icon: ShieldCheckIcon };
    }
  };

  const config = getHealthConfig(health);
  const Icon = config.icon;

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Saúde do Sistema</h3>
          <p className="text-sm text-gray-500">Status atual da infraestrutura</p>
        </div>
        <div className={`p-3 rounded-lg ${config.color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color.replace('bg-', 'bg-').replace('-500', '-100')} ${config.color.replace('bg-', 'text-').replace('-500', '-800')}`}>
            {config.text}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    active_users_today: 0,
    premium_users: 0,
    new_users_this_month: 0,
    total_profiles_saved: 0,
    total_labels_created: 0,
    posts_blocked_today: 0,
    system_health: 'warning',
  });
  const [recentActivity, setRecentActivity] = useState<AdminLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Buscar estatísticas do dashboard
        const dashboardStats = await dashboardService.getDashboardStats();
        setStats(dashboardStats);

        // Buscar atividades recentes
        const activity = await dashboardService.getRecentActivity(5);
        setRecentActivity(activity);
      } catch (err) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Erro ao carregar dados. Tente novamente mais tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seg atrás`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min atrás`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} h atrás`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} dias atrás`;
    }
  };

  const getActivityIcon = (action: string) => {
    if (action.includes('login')) return 'bg-green-400';
    if (action.includes('logout')) return 'bg-gray-400';
    if (action.includes('create') || action.includes('created')) return 'bg-blue-400';
    if (action.includes('update') || action.includes('updated')) return 'bg-yellow-400';
    if (action.includes('delete') || action.includes('deleted')) return 'bg-red-400';
    return 'bg-gray-400';
  };

  const getActivityText = (log: AdminLog) => {
    switch (log.action) {
      case 'user_login':
        return 'Usuário fez login';
      case 'user_logout':
        return 'Usuário fez logout';
      case 'user_created':
        return 'Novo usuário criado';
      case 'user_updated':
        return 'Usuário atualizado';
      case 'settings_changed':
        return 'Configurações alteradas';
      default:
        return log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Carregando dados...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Visão geral do sistema administrativo da extensão Inrock</p>
        {error && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-600">{error}</p>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Usuários"
          value={stats.total_users.toLocaleString()}
          icon={UsersIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Usuários Ativos Hoje"
          value={stats.active_users_today.toLocaleString()}
          icon={UserGroupIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Usuários Premium"
          value={stats.premium_users.toLocaleString()}
          icon={StarIcon}
          color="bg-yellow-500"
        />
        <StatCard
          title="Perfis Salvos"
          value={stats.total_profiles_saved.toLocaleString()}
          icon={DocumentTextIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="Novos Usuários (Mês)"
          value={stats.new_users_this_month.toLocaleString()}
          icon={UsersIcon}
          color="bg-indigo-500"
        />
        <StatCard
          title="Labels Criadas"
          value={stats.total_labels_created.toLocaleString()}
          icon={DocumentTextIcon}
          color="bg-pink-500"
        />
      </div>

      {/* System Health and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SystemHealthCard health={stats.system_health} />
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Atividade Recente</h3>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((log) => (
                <div key={log.id} className="flex items-center space-x-3">
                  <div className={`w-2 h-2 ${getActivityIcon(log.action)} rounded-full`}></div>
                  <span className="text-sm text-gray-600 flex-1">{getActivityText(log)}</span>
                  <span className="text-xs text-gray-400">{formatTimeAgo(log.created_at)}</span>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 text-center py-4">
                Nenhuma atividade recente
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Ações Rápidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            <UsersIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Gerenciar Usuários</span>
          </button>
          <button className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            <ChartBarIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Ver Relatórios</span>
          </button>
          <button className="flex items-center justify-center space-x-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            <CogIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Configurações</span>
          </button>
        </div>
      </div>
    </div>
  );
};
